import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import Select from "react-select";
import schoolApi from "../../api/schoolApi";
import { toast } from "react-toastify";
import { usePermissions } from "../contexts/PermissionContext";
import { saveAs } from "file-saver";
import { 
  Editor, 
  EditorProvider, 
  Toolbar, 
  BtnBold, 
  BtnItalic, 
  BtnUnderline, 
  BtnStrikeThrough, 
  Separator, 
  BtnNumberedList, 
  BtnBulletList, 
  BtnLink, 
  BtnClearFormatting, 
  BtnUndo, 
  BtnRedo 
} from "react-simple-wysiwyg";

const getLocalDatetimeString = (d = new Date()) => {
  const pad = (n) => String(n).padStart(2, '0');
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const CreateTaskModal = (props) => {
  const { userInfo } = usePermissions();
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "Medium",
    status: "Pending",
    due_date: "",
    assign_date: getLocalDatetimeString(),
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDownload = async () => {
    if (!props.task?.file_id) return;
    try {
      const response = await schoolApi.downloadFiles(props.task.file_id);
      const blob = new Blob([response], { type: response.type });
      saveAs(blob, props.task.file_title || "task-attachment");
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file");
    }
  };

  useEffect(() => {
    fetchUsers();
    setFileError("");
    if (props.task) {
      setFormData({
        title: props.task.title || "",
        description: props.task.description || "",
        priority: props.task.priority || "Medium",
        status: props.task.status || "Pending",
        due_date: props.task.due_date ? getLocalDatetimeString(new Date(props.task.due_date)) : "",
        assign_date: props.task.assign_date ? getLocalDatetimeString(new Date(props.task.assign_date)) : "",
      });
      
      let users = props.task.assigned_users;
      if (typeof users === 'string') {
        try { users = JSON.parse(users); } catch (e) { users = []; }
      }
      
      if (Array.isArray(users)) {
        const mappedUsers = users.map(u => ({ 
          value: u.user_id, 
          label: u.name 
        }));
        setSelectedUsers(mappedUsers);
      } else {
        setSelectedUsers([]);
      }
    } else {
      setFormData({
        title: "",
        description: "",
        priority: "Medium",
        status: "Pending",
        due_date: "",
        assign_date: getLocalDatetimeString(),
      });
      setSelectedUsers([]);
      setFile(null);
    }
  }, [props.task, props.show]);

  const fetchUsers = async () => {
    try {
      const result = await schoolApi.fetchUsersbyCompanyId("active");
      if (result && result.success && result.userRec) {
        const options = result.userRec
          .filter(u => !u.status || u.status.toLowerCase() === "active")
          .map(u => ({
            value: u.id,
            label: `${u.firstname} ${u.lastname || ''} (${u.userrole})`
          }));
        setUsers(options);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (fileError || (file && file.size > 10 * 1024 * 1024)) {
      toast.error("File size exceeds 10MB limit. Task cannot be created with files larger than 10MB.");
      return;
    }
    if (selectedUsers.length === 0) {
      toast.warning("Please select at least one user to assign.");
      return;
    }
    if (formData.due_date == "") {
      toast.warning("Please select a due date.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        created_by_id: userInfo.id,
        last_modified_by_id: userInfo.id
      };

      let result;
      const assignedUserIds = selectedUsers.map(u => u.value);
      
      if (props.task) {
        result = await schoolApi.updateTask(props.task.id, payload, assignedUserIds);
      } else {
        result = await schoolApi.createTask(payload, assignedUserIds);
      }

      if (result.success) {
        const taskId = props.task ? props.task.id : result.result.id;
        
        if (file) {
          const formDataFile = new FormData();
          formDataFile.append("files0", file);
          formDataFile.append("description", "Task Attachment");
          await schoolApi.createFile(taskId, formDataFile);
        }

        toast.success(props.task ? "Task updated successfully" : "Task created successfully");
        props.refresh();
        props.onHide();
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      toast.error("An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal {...props} size="lg" aria-labelledby="task-modal-title" centered scrollable className="custom-task-modal">
      <Modal.Header closeButton className="border-0 bg-white pt-4 px-4">
        <Modal.Title id="task-modal-title" className="fw-bold text-dark d-flex align-items-center">
          <div className="rounded-3 bg-primary bg-opacity-10 text-primary p-2 me-3">
            <i className={`fa-solid ${props.task ? 'fa-pen-to-square' : 'fa-plus-circle'}`}></i>
          </div>
          {props.task ? "Edit Task" : "Create New Task"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 pb-4">
        <Form onSubmit={handleSubmit} className="custom-form">
          <Form.Group className="mb-4">
            <Form.Label className="text-muted small fw-bold text-uppercase mb-2">Task Title</Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              placeholder="What needs to be done?"
              className="form-control-lg border-0 bg-light rounded-3"
            />
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label className="text-muted small fw-bold text-uppercase mb-2">Description</Form.Label>
            <div className="custom-editor-container border-0 bg-light rounded-3 overflow-visible">
              <EditorProvider>
                <Editor
                  value={formData.description || ""}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  style={{ minHeight: "200px", border: 'none', background: 'transparent' }}
                >
                  <Toolbar>
                    <BtnUndo /><BtnRedo /><Separator />
                    <BtnBold /><BtnItalic /><BtnUnderline /><BtnStrikeThrough /><Separator />
                    <BtnNumberedList /><BtnBulletList /><Separator />
                    <BtnLink /><BtnClearFormatting />
                  </Toolbar>
                </Editor>
              </EditorProvider>
            </div>
          </Form.Group>

          <Row className="mb-4">
            <Col md={6}>
              <Form.Group className="mb-3 mb-md-0">
                <Form.Label className="text-muted small fw-bold text-uppercase mb-2">Assign Date</Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="assign_date"
                  value={formData.assign_date}
                  onChange={handleInputChange}
                  className="border-0 bg-light rounded-3"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="text-muted small fw-bold text-uppercase mb-2">Due Date</Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="due_date"
                  value={formData.due_date}
                  onChange={handleInputChange}
                  className="border-0 bg-light rounded-3"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={6}>
              <Form.Group className="mb-3 mb-md-0">
                <Form.Label className="text-muted small fw-bold text-uppercase mb-2">Priority Level</Form.Label>
                <Form.Select 
                  name="priority" 
                  value={formData.priority} 
                  onChange={handleInputChange}
                  className="border-0 bg-light rounded-3"
                >
                  <option value="Low">🔵 Low</option>
                  <option value="Medium">🟡 Medium</option>
                  <option value="High">🔴 High</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="text-muted small fw-bold text-uppercase mb-2">Current Status</Form.Label>
                <Form.Select 
                  name="status" 
                  value={formData.status} 
                  onChange={handleInputChange}
                  className="border-0 bg-light rounded-3"
                >
                  <option value="Pending">🕒 Pending</option>
                  <option value="In Progress">⚙️ In Progress</option>
                  <option value="Submitted">📤 Submitted</option>
                  <option value="Approved">✅ Approved</option>
                  <option value="Rejected">❌ Rejected</option>
                  <option value="Completed">✨ Completed</option>
                  <option value="Cancelled">🚫 Cancelled</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-4">
            <Form.Label className="text-muted small fw-bold text-uppercase mb-2">Assign To Team Members</Form.Label>
            <Select
              isMulti
              options={users}
              value={selectedUsers}
              onChange={setSelectedUsers}
              placeholder="Search and select team members..."
              classNamePrefix="custom-select"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                control: (base) => ({
                  ...base,
                  backgroundColor: '#f8f9fa',
                  border: 'none',
                  borderRadius: '0.75rem',
                  padding: '4px',
                  boxShadow: 'none',
                }),
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: '#fff',
                  borderRadius: '6px',
                  border: '1px solid #e9ecef',
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: '#495057',
                  fontWeight: '500',
                }),
                multiValueRemove: (base) => ({
                  ...base,
                  color: '#adb5bd',
                  ':hover': { backgroundColor: '#ffefef', color: '#dc3545' },
                }),
              }}
            />
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label className="text-muted small fw-bold text-uppercase mb-2">Attachment (Optional)</Form.Label>
            <div className={`file-upload-wrapper bg-light rounded-3 p-3 text-center border-2 border-dashed ${fileError ? 'border-danger bg-danger bg-opacity-10' : 'border-secondary border-opacity-25'}`}>
              <Form.Control
                type="file"
                onChange={(e) => {
                  const selectedFile = e.target.files[0];
                  if (selectedFile) {
                    if (selectedFile.size > 10 * 1024 * 1024) {
                      const msg = `File size (${(selectedFile.size / (1024 * 1024)).toFixed(1)}MB) exceeds 10MB limit. Task cannot be created with this file.`;
                      toast.error(msg);
                      setFile(selectedFile);
                      setFileError(msg);
                      return;
                    }
                    setFile(selectedFile);
                    setFileError("");
                  }
                }}
                className="d-none"
                id="task-file-input"
              />
              <label htmlFor="task-file-input" className="mb-0 cursor-pointer w-100">
                <div className="d-flex flex-column align-items-center">
                  <i className={`fa-solid ${fileError ? 'fa-triangle-exclamation fs-3 text-danger' : 'fa-cloud-arrow-up fs-3 text-primary'} mb-2`}></i>
                  <span className={`${fileError ? 'text-danger fw-bold' : 'text-dark fw-medium'}`}>
                    {file ? file.name : "Click to upload or drag & drop"}
                  </span>
                  <span className={`${fileError ? 'text-danger small' : 'text-muted small'}`}>
                    {fileError ? fileError : "Max file size: 10MB"}
                  </span>
                </div>
              </label>
              {file && (
                <div className="mt-2">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="rounded-pill py-0 px-2"
                    onClick={(e) => {
                      e.preventDefault();
                      setFile(null);
                      setFileError("");
                      const input = document.getElementById("task-file-input");
                      if (input) input.value = "";
                    }}
                  >
                    <i className="fa-solid fa-times me-1"></i> Remove File
                  </Button>
                </div>
              )}
            </div>
            {fileError && (
              <div className="alert alert-danger py-2 px-3 mt-2 mb-0 small d-flex align-items-center justify-content-between rounded-3">
                <span>
                  <i className="fa-solid fa-circle-exclamation me-2"></i>
                  {fileError}
                </span>
                <span className="badge bg-danger">Button Disabled</span>
              </div>
            )}
            {props.task?.file_id && (
              <div className="mt-2 d-flex align-items-center">
                <span className="text-muted small me-2">Existing file:</span>
                <Button variant="link" size="sm" className="p-0 text-decoration-none fw-bold" onClick={handleDownload}>
                  <i className="fa-solid fa-paperclip me-1"></i> View Attachment
                </Button>
              </div>
            )}
          </Form.Group>

        </Form>
      </Modal.Body>
      <Modal.Footer className="border-0 bg-white px-4 pb-4 pt-0 gap-3">
        <Button variant="light" onClick={props.onHide} className="px-4 py-2 fw-bold text-muted rounded-3 border-0">
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={Boolean(fileError) || isSubmitting}
          className="px-5 py-2 fw-bold rounded-3 shadow-sm border-0"
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            props.task ? "Save Changes" : "Create Task"
          )}
        </Button>
      </Modal.Footer>
      <style>{`
        .custom-task-modal .modal-content {
          border-radius: 1.25rem;
          border: none;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }
        .custom-form .form-control:focus, .custom-form .form-select:focus {
          background-color: #f1f3f5;
          box-shadow: none;
          border: 1px solid #0d6efd;
        }
        .custom-editor-container .rsw-editor {
          background: transparent !important;
          border: none !important;
        }
        .custom-editor-container .rsw-toolbar {
          background: #f1f3f5 !important;
          border-bottom: 1px solid #e9ecef !important;
          padding: 8px !important;
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .border-dashed {
          border-style: dashed !important;
        }
      `}</style>
    </Modal>
  );
};

export default CreateTaskModal;
